import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { id } from 'date-fns/locale';
import { toast } from 'sonner';
import { useApp } from '../contexts/AppContext';
import { 
  MessageSquare, Calendar, DollarSign, Settings, 
  TrendingUp, Users, Filter, Download, RefreshCw,
  ChevronDown, Save
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WhatsAppCost = () => {
  const [loading, setLoading] = useState(true);
  const [usageData, setUsageData] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('all');
  const [dateRange, setDateRange] = useState({
    start: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    end: format(endOfMonth(new Date()), 'yyyy-MM-dd')
  });
  const [costSettings, setCostSettings] = useState({
    price_per_wa: 400,
    currency: 'IDR'
  });
  const [showSettings, setShowSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [userRole, setUserRole] = useState('');
  // Delta #53: hormati filter cabang global di header
  const { selectedBranch } = useApp();

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setUserRole(user.role || '');
    
    fetchData();
    if (user.role === 'super_admin') {
      fetchTenants();
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [selectedTenant, dateRange, selectedBranch]);

  const fetchTenants = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/v2/tenants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenants(response.data.data || []);
    } catch (error) {
      console.error('Error fetching tenants:', error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams({
        start_date: dateRange.start,
        end_date: dateRange.end
      });
      
      if (selectedTenant !== 'all') {
        params.append('tenant_id', selectedTenant);
      }
      if (selectedBranch && selectedBranch !== 'all') {
        params.append('branch_id', selectedBranch);
      }
      
      const [usageRes, settingsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/v2/whatsapp/usage?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/v2/whatsapp/cost-settings${selectedTenant !== 'all' ? `?tenant_id=${selectedTenant}` : ''}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setUsageData(usageRes.data.data);
      setCostSettings(settingsRes.data.data || { price_per_wa: 400, currency: 'IDR' });
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  const saveCostSettings = async () => {
    try {
      setSavingSettings(true);
      const token = localStorage.getItem('token');
      
      await axios.put(
        `${BACKEND_URL}/api/v2/whatsapp/cost-settings`,
        {
          ...costSettings,
          tenant_id: selectedTenant !== 'all' ? selectedTenant : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Pengaturan biaya berhasil disimpan');
      setShowSettings(false);
      fetchData();
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Gagal menyimpan pengaturan');
    } finally {
      setSavingSettings(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const exportToCSV = () => {
    if (!usageData?.details) return;
    
    const headers = ['Tanggal', 'Tipe Pesan', 'Jumlah', 'Harga/WA', 'Total Biaya'];
    const rows = usageData.details.map(d => [
      d.date,
      d.message_type,
      d.count,
      costSettings.price_per_wa,
      d.count * costSettings.price_per_wa
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `whatsapp-cost-${dateRange.start}-${dateRange.end}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600 dark:text-indigo-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-3 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <svg className="w-6 h-6 sm:w-7 sm:h-7 text-green-600 dark:text-green-400 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Biaya WhatsApp
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1 dark:text-slate-400">
            Monitoring penggunaan dan biaya WhatsApp per tenant
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSettings(!showSettings)}
          >
            <Settings className="w-4 h-4 mr-1" />
            Pengaturan
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={exportToCSV}
          >
            <Download className="w-4 h-4 mr-1" />
            Export CSV
          </Button>
          <Button
            size="sm"
            onClick={fetchData}
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <h3 className="font-semibold text-slate-800 dark:text-white mb-4">
            Pengaturan Biaya WhatsApp
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Harga per WhatsApp (Rp)</Label>
              <Input
                type="number"
                value={costSettings.price_per_wa}
                onChange={(e) => setCostSettings({
                  ...costSettings,
                  price_per_wa: parseInt(e.target.value) || 0
                })}
                placeholder="400"
              />
            </div>
            <div className="space-y-2">
              <Label>Berlaku untuk</Label>
              <Input
                value={selectedTenant === 'all' ? 'Semua Tenant (Default)' : tenants.find(t => t.tenant_id === selectedTenant)?.tenant_name || 'Tenant Terpilih'}
                disabled
                className="bg-slate-100 dark:bg-slate-800"
              />
            </div>
            <div className="flex items-end">
              <Button onClick={saveCostSettings} disabled={savingSettings}>
                <Save className="w-4 h-4 mr-1" />
                {savingSettings ? 'Menyimpan...' : 'Simpan'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
        <div className="flex flex-wrap items-center gap-4">
          {userRole === 'super_admin' && (
            <div className="space-y-1">
              <Label className="text-xs text-slate-500 dark:text-slate-400">Tenant</Label>
              <Select value={selectedTenant} onValueChange={setSelectedTenant}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Semua Tenant" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Tenant</SelectItem>
                  {tenants.map(tenant => (
                    <SelectItem key={tenant.tenant_id} value={tenant.tenant_id}>
                      {tenant.tenant_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          <div className="space-y-1">
            <Label className="text-xs text-slate-500 dark:text-slate-400">Dari Tanggal</Label>
            <Input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              className="w-[160px]"
            />
          </div>
          
          <div className="space-y-1">
            <Label className="text-xs text-slate-500 dark:text-slate-400">Sampai Tanggal</Label>
            <Input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              className="w-[160px]"
            />
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-green-100 dark:bg-green-900/30 rounded-md">
              <MessageSquare className="w-4 h-4 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">Total WA Terkirim</p>
              <p className="text-sm font-semibold text-slate-800 dark:text-white">
                {usageData?.total_sent?.toLocaleString('id-ID') || 0}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-red-100 dark:bg-red-900/30 rounded-md">
              <MessageSquare className="w-4 h-4 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">WA Gagal</p>
              <p className="text-sm font-semibold text-slate-800 dark:text-white">
                {usageData?.total_failed?.toLocaleString('id-ID') || 0}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-md">
              <DollarSign className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">Harga per WA</p>
              <p className="text-sm font-semibold text-slate-800 dark:text-white">
                Rp {(costSettings?.price_per_wa || 0).toLocaleString('id-ID')}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-indigo-100 dark:bg-indigo-900/30 rounded-md">
              <TrendingUp className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">Total Biaya</p>
              <p className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                Rp {((usageData?.total_sent || 0) * (costSettings?.price_per_wa || 0)).toLocaleString('id-ID')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Usage by Type */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
        <h3 className="font-semibold text-slate-800 dark:text-white mb-4">
          Penggunaan per Tipe Pesan
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700">
                <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300">Tipe Pesan</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300">Jumlah</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300">Harga/WA</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300">Total Biaya</th>
              </tr>
            </thead>
            <tbody>
              {usageData?.by_type?.map((item, idx) => (
                <tr key={idx} className="border-b border-slate-100 dark:border-slate-700/50">
                  <td className="py-3 px-3">
                    <Badge variant="outline" className="capitalize">
                      {item.type.replace('_', ' ')}
                    </Badge>
                  </td>
                  <td className="py-3 px-3 text-right font-mono text-sm">
                    {item.count.toLocaleString('id-ID')}
                  </td>
                  <td className="py-3 px-3 text-right font-mono text-sm">
                    {formatCurrency(costSettings?.price_per_wa || 0)}
                  </td>
                  <td className="py-3 px-3 text-right font-medium text-sm text-indigo-600 dark:text-indigo-400">
                    {formatCurrency(item.count * (costSettings?.price_per_wa || 0))}
                  </td>
                </tr>
              ))}
              {(!usageData?.by_type || usageData.by_type.length === 0) && (
                <tr>
                  <td colSpan={4} className="py-8 text-center text-slate-500 dark:text-slate-400">
                    Tidak ada data untuk periode ini
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr className="bg-slate-100 dark:bg-slate-700/60 font-semibold text-sm text-slate-800 dark:text-slate-100">
                <td className="py-3 px-3">TOTAL</td>
                <td className="py-3 px-3 text-right font-mono">
                  {usageData?.total_sent?.toLocaleString('id-ID') || 0}
                </td>
                <td className="py-3 px-3 text-right">-</td>
                <td className="py-3 px-3 text-right text-indigo-600 dark:text-indigo-400">
                  {formatCurrency((usageData?.total_sent || 0) * (costSettings?.price_per_wa || 0))}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Usage by Tenant (Super Admin only) */}
      {userRole === 'super_admin' && selectedTenant === 'all' && usageData?.by_tenant && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <h3 className="font-semibold text-slate-800 dark:text-white mb-4">
            Penggunaan per Tenant
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300">Tenant</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300">WA Terkirim</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300">WA Gagal</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300">Harga/WA</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300">Total Biaya</th>
                </tr>
              </thead>
              <tbody>
                {usageData.by_tenant.map((tenant, idx) => (
                  <tr key={idx} className="border-b border-slate-100 dark:border-slate-700/50">
                    <td className="py-3 px-3 font-medium">{tenant.tenant_name || 'Unknown'}</td>
                    <td className="py-3 px-3 text-right font-mono text-sm text-green-600 dark:text-green-400">
                      {tenant.sent.toLocaleString('id-ID')}
                    </td>
                    <td className="py-3 px-3 text-right font-mono text-sm text-red-600 dark:text-red-400">
                      {tenant.failed.toLocaleString('id-ID')}
                    </td>
                    <td className="py-3 px-3 text-right font-mono text-sm">
                      {formatCurrency(tenant.price_per_wa || costSettings.price_per_wa)}
                    </td>
                    <td className="py-3 px-3 text-right font-semibold text-indigo-600 dark:text-indigo-400">
                      {formatCurrency(tenant.sent * (tenant.price_per_wa || costSettings.price_per_wa))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppCost;
